import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformAdminAuth } from '../contexts/PlatformAdminAuthContext';

/**
 * Platform Admin Top Bar Component
 * 
 * Top navigation bar for platform admin portal with:
 * - AstraPeak Platform Admin branding
 * - Platform admin user info
 * - Logout functionality
 * - Menu toggle for sidebar
 */

interface PlatformAdminTopBarProps {
  platformAdmin: any;
  onMenuClick: () => void;
}

export const PlatformAdminTopBar: React.FC<PlatformAdminTopBarProps> = ({ 
  platformAdmin, 
  onMenuClick 
}) => {
  const navigate = useNavigate();
  const { logout } = usePlatformAdminAuth();
  const [showUserMenu, setShowUserMenu] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/platform-admin/login');
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-gray-900 border-b border-gray-800 z-50">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left: Menu Toggle + Branding */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-md hover:bg-gray-800 transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <img 
            src="/astrapeak-logo.svg" 
            alt="AstraPeak Platform Admin" 
            className="h-10 w-auto max-w-[160px] object-contain"
            onError={(e) => {
              // Fallback to gradient box with text if logo fails to load
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling;
              if (fallback) fallback.classList.remove('hidden');
            }}
          />
          <div className="hidden flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">AstraPeak</h1>
              <p className="text-xs text-purple-400">Platform Admin</p>
            </div>
          </div>
        </div>

        {/* Right: Platform Admin Info + Logout */}
        <div className="flex items-center space-x-4">
          {/* System Health Indicator */}
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-900/30 border border-green-700/50 rounded-md">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-xs text-green-300 font-medium">System Healthy</span>
          </div>

          {/* Platform Admin User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">
                    {platformAdmin?.username?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-white">{platformAdmin?.username || 'Admin'}</p>
                  <p className="text-xs text-gray-400">
                    {platformAdmin?.is_super_admin ? 'Super Admin' : 'Platform Admin'}
                  </p>
                </div>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-md shadow-lg py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-700">
                  <p className="text-sm font-medium text-white">{platformAdmin?.username}</p>
                  <p className="text-xs text-gray-400">{platformAdmin?.email}</p>
                </div>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/platform-admin/profile');
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Profile Settings
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default PlatformAdminTopBar;
